/**
 * MoverPositionStore — out-of-React channel for mover-position updates.
 *
 * The component's old animation contract was:
 *   1. Consumer holds positions in React state.
 *   2. State change → new config object → XtsViewer3D normalize +
 *      reconcile + per-XPU re-render → mover groups update.
 *
 * For 60-Hz updates that's 60 full reconciliations per second across
 * the entire scene tree. The store turns this into a one-line
 * imperative call that bypasses React entirely:
 *
 *   viewerRef.current.setMoverPosition(42, 1234);
 *
 * The store is read by `<XtsMover>` inside `useFrame` — Three.js
 * group transforms get mutated in place, no React render scheduled.
 *
 * Backward compat: when no entry exists for a mover.id, the
 * `<XtsMover>` falls back to the prop-driven `partPositionMm` from
 * config. So the existing config-only contract still works.
 *
 * Each entry tracks both the user-frame position AND (optionally) the
 * `partObjectId` of the part the mover is currently on. The latter is
 * populated by the array form of
 * `XtsViewer3DRef.setMoverPositions(MoverPositionEntry[])`; the
 * single-arg `set(moverId, mm)` form leaves it undefined (the mover
 * stays on its config-time part).
 */

import React from 'react';

export interface MoverPositionStoreEntry {
  partPositionMm: number;
  /** = PartConfig.objectId. Optional — undefined means "stay on config part". */
  partObjectId?: number;
}

export class MoverPositionStore {
  private readonly entries = new Map<number, MoverPositionStoreEntry>();
  /** Monotonic counter incremented on every write. Used by demand rendering
   *  to detect activity without comparing all positions. */
  version = 0;
  /** Optional callback invoked after any position write. Set by the
   *  demand-rendering hook to trigger frame invalidation. */
  onWrite: (() => void) | null = null;

  set(moverId: number, partPositionMm: number): void {
    const existing = this.entries.get(moverId);
    if (existing) {
      existing.partPositionMm = partPositionMm;
    } else {
      this.entries.set(moverId, { partPositionMm });
    }
    this.version++;
    this.onWrite?.();
  }

  setMany(record: Record<number, number>): void {
    for (const [id, mm] of Object.entries(record)) {
      const existing = this.entries.get(Number(id));
      if (existing) {
        existing.partPositionMm = mm;
      } else {
        this.entries.set(Number(id), { partPositionMm: mm });
      }
    }
    this.version++;
    this.onWrite?.();
  }

  /** Set both position and current part (from the array-form API). */
  setEntry(
    moverId: number,
    partObjectId: number,
    partPositionMm: number,
  ): void {
    const existing = this.entries.get(moverId);
    if (existing) {
      existing.partPositionMm = partPositionMm;
      existing.partObjectId = partObjectId;
    } else {
      this.entries.set(moverId, { partPositionMm, partObjectId });
    }
    this.version++;
    this.onWrite?.();
  }

  get(moverId: number): number | undefined {
    return this.entries.get(moverId)?.partPositionMm;
  }

  getEntry(moverId: number): MoverPositionStoreEntry | undefined {
    return this.entries.get(moverId);
  }

  getPartObjectId(moverId: number): number | undefined {
    return this.entries.get(moverId)?.partObjectId;
  }

  has(moverId: number): boolean {
    return this.entries.has(moverId);
  }

  clear(moverId: number): void {
    this.entries.delete(moverId);
  }

  clearAll(): void {
    this.entries.clear();
  }
}

/**
 * React context the scene tree uses to find the active store. Set up
 * by `<XtsViewer3D>` so every `<XtsMover>` reads the same instance.
 */
export const MoverPositionStoreContext =
  React.createContext<MoverPositionStore | null>(null);
